import { MessageSquare } from 'lucide-react';
import { Button } from '../ui/Button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/Dialog';

const MAX_COMMENT_LENGTH = 10000;

// Shared comment block for the two status/disposition modals. CommentModal
// (carrier status→reason) and SlDispositionModal (SL response→reason→action)
// render different cascades above this, but an identical Comment section below:
// a textarea plus the "Externally Viewable" and request-response checkboxes.
// This is that section. `idPrefix` namespaces element ids/test ids so the two
// modals keep their own selectors and never collide when both are in the DOM.
function CommentComposer({
  comment,
  onCommentChange,
  textareaRef,
  placeholder = 'Add your comment...',
  maxLength = MAX_COMMENT_LENGTH,
  showExternal,
  onShowExternalChange,
  // CommentModal only shows this for SL employees; the SL modal is SL-only so
  // it's always visible there.
  showExternalVisible = true,
  needsReply,
  onNeedsReplyChange,
  needsReplyLabel = 'Request response',
  // CommentModal flags a typed-but-unsent comment with a "Pending" badge; the
  // SL modal doesn't.
  showPendingBadge = false,
  // Tailwind min-height class for the textarea; the SL modal uses a taller box.
  commentMinHeightClass = 'min-h-[80px]',
  idPrefix = '',
}) {
  const showPending = showPendingBadge && comment.trim().length > 0;
  return (
    <section>
      <div className="flex gap-2 items-center">
        <MessageSquare className="h-4 w-4 text-primary" aria-hidden="true" />
        <span className="text-sm font-bold tracking-tight text-foreground">Comment</span>
        {showPending && (
          <span
            data-testid="pending-badge"
            className="inline-flex items-center h-4 px-1.5 py-0 text-[10px] font-medium bg-primary/10 text-primary border border-primary/20 rounded-full"
          >
            Pending
          </span>
        )}
      </div>
      <div className="mt-2 border border-border bg-muted/30 p-4 rounded-lg">
        <textarea
          id={`${idPrefix}comment-textarea`}
          ref={textareaRef}
          value={comment}
          onChange={onCommentChange}
          maxLength={maxLength}
          placeholder={placeholder}
          aria-label="Comment"
          className={`w-full ${commentMinHeightClass} resize-none text-sm border border-input rounded-md px-3 py-2 bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-ring`}
        />

        {showExternalVisible && (
          <div className="mt-2 flex items-center gap-2">
            <input
              id={`${idPrefix}externally-viewable-checkbox`}
              type="checkbox"
              checked={showExternal}
              onChange={(e) => onShowExternalChange(e.target.checked)}
              className="h-4 w-4 rounded border-input accent-primary"
            />
            <label
              htmlFor={`${idPrefix}externally-viewable-checkbox`}
              className="text-xs cursor-pointer text-foreground"
            >
              Externally Viewable
            </label>
          </div>
        )}

        <div className="mt-3 flex items-center gap-2" data-testid={`${idPrefix}needs-reply-row`}>
          <input
            id={`${idPrefix}needs-reply`}
            type="checkbox"
            checked={needsReply}
            onChange={(e) => onNeedsReplyChange(e.target.checked)}
            className="h-4 w-4 rounded border-input accent-primary"
          />
          <label
            htmlFor={`${idPrefix}needs-reply`}
            className="text-xs cursor-pointer text-foreground"
          >
            {needsReplyLabel}
          </label>
        </div>
      </div>
    </section>
  );
}

// Shared submit footer: the error banner (with inline Retry), the success
// banner, and the Submit row. Identical structure across both modals; the
// differing bits (success copy, whether a Cancel button shows, test ids) are
// props. `submitDisabled` is OR'd with the success state so a posted form
// can't be re-submitted during the auto-close delay.
function CommentSubmitFooter({
  status,
  errorMessage,
  onSubmit,
  submitDisabled,
  onCancel,
  successMessage = 'Comment posted.',
  submitLabel = 'Submit',
  submittingLabel = 'Submitting...',
  submitTestId,
  idPrefix = '',
}) {
  return (
    <>
      {status === 'error' && (
        <div
          role="alert"
          data-testid={`${idPrefix}submit-error`}
          className="flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive"
        >
          <span>{errorMessage}</span>
          <Button
            type="button"
            onClick={onSubmit}
            className="h-8 px-3 rounded-md bg-destructive text-destructive-foreground text-xs font-medium hover:bg-destructive/90"
          >
            Retry
          </Button>
        </div>
      )}

      {status === 'success' && (
        <div
          role="status"
          data-testid={`${idPrefix}submit-success`}
          className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-700"
        >
          {successMessage}
        </div>
      )}

      {status !== 'error' && (
        <div className="flex justify-end gap-2 mt-2">
          {onCancel && (
            <Button
              type="button"
              onClick={onCancel}
              className="h-9 px-3 rounded-md border border-input bg-background text-foreground text-sm font-medium hover:bg-accent"
            >
              Cancel
            </Button>
          )}
          <Button
            type="button"
            data-testid={submitTestId}
            onClick={onSubmit}
            disabled={submitDisabled || status === 'success'}
            className="h-9 px-3 rounded-md bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none"
          >
            {status === 'submitting' ? submittingLabel : submitLabel}
          </Button>
        </div>
      )}
    </>
  );
}

// Shared modal shell for the comment/disposition modals. Owns the entire outer
// frame — Dialog (backdrop-click disabled, like the CP composer), the
// fixed-width DialogContent, the header, and the body layout — so both modals
// render through ONE shell and can only differ in their cascade `children` and
// config (title, labels, success copy). The width lives here alone: a definite
// `sm:w-[680px]` rather than a max-width, because the Dialog wrapper shrink-
// wraps its content — a max-width lets a narrow cascade render narrower than a
// wide one. A definite width keeps both modals identical regardless of content.
function CommentActionModal({
  open,
  onClose,
  title,
  contentTestId,
  idPrefix = '',
  children,
  // composer
  comment,
  onCommentChange,
  textareaRef,
  showExternal,
  onShowExternalChange,
  showExternalVisible = true,
  needsReply,
  onNeedsReplyChange,
  needsReplyLabel = 'Request response',
  showPendingBadge = false,
  commentMinHeightClass,
  // footer
  status,
  errorMessage,
  onSubmit,
  submitDisabled,
  onCancel,
  successMessage,
  submitTestId,
}) {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next && onClose) onClose();
      }}
      dismissOnBackdropClick={false}
    >
      {/* Definite width (not just max-w): the Dialog wrapper shrink-wraps its
          content, so a max-width alone lets a narrow modal (the SL cascade)
          render narrower than a wide one (CP status). A fixed width keeps both
          modals identical. */}
      <DialogContent
        onClose={onClose}
        data-testid={contentTestId}
        className="w-full sm:w-[680px] sm:max-w-[680px] p-6 rounded-lg bg-background"
      >
        <DialogHeader className="pb-4">
          <DialogTitle className="text-lg font-semibold tracking-tight">{title}</DialogTitle>
        </DialogHeader>

        <div className="space-y-6">
          {children}

          <CommentComposer
            idPrefix={idPrefix}
            comment={comment}
            onCommentChange={onCommentChange}
            textareaRef={textareaRef}
            showExternal={showExternal}
            onShowExternalChange={onShowExternalChange}
            showExternalVisible={showExternalVisible}
            needsReply={needsReply}
            onNeedsReplyChange={onNeedsReplyChange}
            needsReplyLabel={needsReplyLabel}
            showPendingBadge={showPendingBadge}
            commentMinHeightClass={commentMinHeightClass}
          />

          <CommentSubmitFooter
            idPrefix={idPrefix}
            status={status}
            errorMessage={errorMessage}
            onSubmit={onSubmit}
            submitDisabled={submitDisabled}
            onCancel={onCancel}
            successMessage={successMessage}
            submitTestId={submitTestId}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}

export { CommentComposer, CommentSubmitFooter, CommentActionModal };
